import { Card, CardContent, Grid, IconButton, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { BugReport, ControlPointDuplicate, MoreVert, Settings, Store } from '@mui/icons-material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../session/InternalAuthProvider';
import { useLanguage } from '../../common/GeneralUtils';
import { AccountData, AccountService, PassOrderPageData } from '../../api';
import CopyToClipboardButton from '../../calendar/dialogs/CopyToClipboardButton';
import { priceFormatterFromCurrency } from '../../utils/utils';
import { WorkshopCircle } from '../../common/ui/WorkshopCircle';
import ConfirmationDialog from '../../common/ui/ConfimationDialog';
import { useSafeExecute } from '../../common/apiNotifications/safeExecute';
import { useGetAccountAuthenticated } from '../../session/swr/useGetAccountAuthenticated';
import { PassTypeChip } from './PassTypeChip';

type PassOrderPageCardProps = {
  passOrderPage: PassOrderPageData;
};

const PassOrderPageCard = ({ passOrderPage }: PassOrderPageCardProps) => {
  const theme = useTheme();
  const { authState } = useAuth();
  const account = authState?.account as unknown as AccountData;
  const language = useLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const safeExecute = useSafeExecute();
  const { updateLocalAccount } = useGetAccountAuthenticated({});

  const bookingPageLink = `${import.meta.env.VITE_REACT_APP_APP_DOMAIN}/pass/${account.id}/${passOrderPage.id}`;

  const handleClone = async (confirmed: boolean) => {
    setCloneDialogOpen(false);
    if (!confirmed) return;
    const existingIds = new Set(account.pass_order_pages?.map((p: { id: string }) => p.id));
    const updatedAccount = await safeExecute({
      apiCall: () => AccountService.clonePassOrderPage({ pass_order_page_id: passOrderPage.id }),
      successMessage: t('order_pages.clone_success'),
    });
    await updateLocalAccount(updatedAccount);
    const newPage = updatedAccount?.pass_order_pages?.find((p: { id: string }) => !existingIds.has(p.id));
    if (newPage) navigate(`/dashboard/pass-settings/${newPage.id}`);
  };

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);

  return (
    <>
      <Card
        sx={{
          width: { xs: '100%', sm: 260 },
          cursor: 'pointer',
        }}
        onClick={() => navigate(`/dashboard/pass-settings/${passOrderPage.id}`)}>
        <CardContent sx={{ p: 2, pb: '16px !important' }}>
          <Grid container justifyContent="space-between" alignItems="center" mb={0.5} flexWrap="nowrap">
            <PassTypeChip passType={passOrderPage.pass_type} />
            <IconButton size="small" onClick={handleMenuOpen}>
              <MoreVert fontSize="small" />
            </IconButton>
          </Grid>

          <Typography
            variant="subtitle2"
            fontWeight={600}
            mb={0.5}
            sx={{
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {passOrderPage.label[language]}
          </Typography>

          <Typography variant="caption" color="text.secondary" display="block">
            {priceFormatterFromCurrency(passOrderPage.price, account.currency)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            {passOrderPage.num_punches != null
              ? t('passes.punches_count', { count: passOrderPage.num_punches })
              : t('passes.unlimited_punches')}{' '}
            &middot; {t('passes.validity_days_count', { count: passOrderPage.validity_days })}
          </Typography>

          {(passOrderPage.redeemable_on?.length ?? 0) > 0 && (
            <Grid container gap={0.5} mb={0.5}>
              {passOrderPage.redeemable_on?.map((item) => (
                <WorkshopCircle
                  key={item.order_page_id}
                  account={account}
                  orderPageId={item.order_page_id}
                  language={language}
                />
              ))}
            </Grid>
          )}

          <Grid container alignItems="center" gap={0.5} onClick={(e) => e.stopPropagation()}>
            <CopyToClipboardButton
              orderPageId={passOrderPage.id}
              text={bookingPageLink}
              color={theme.palette.text.secondary}
              width="20px"
            />
            <Typography
              variant="subtitle2"
              component="a"
              href={bookingPageLink}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              {t('passes.booking_page')}
            </Typography>
          </Grid>
        </CardContent>
      </Card>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleMenuClose} component={Link} to={`/dashboard/pass-settings/${passOrderPage.id}`}>
          <Settings fontSize="small" sx={{ marginInlineEnd: 1 }} />
          {t('order_pages.settings')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            window.open(bookingPageLink, '_blank');
          }}>
          <Store fontSize="small" sx={{ marginInlineEnd: 1 }} />
          {t('passes.booking_page')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            window.open(`${bookingPageLink}?troubleshoot=true`, '_blank');
          }}>
          <BugReport fontSize="small" sx={{ marginInlineEnd: 1 }} />
          {t('order_pages.troubleshoot')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setCloneDialogOpen(true);
          }}>
          <ControlPointDuplicate fontSize="small" sx={{ marginInlineEnd: 1 }} />
          {t('order_pages.clone')}
        </MenuItem>
      </Menu>

      <ConfirmationDialog
        isOpen={cloneDialogOpen}
        onClose={handleClone}
        title={t('order_pages.clone')}
        content={t('passes.clone_confirmation')}
        ctaText={t('order_pages.clone')}
        confirmButtonColor="primary"
      />
    </>
  );
};

export default PassOrderPageCard;
